import { useState, useRef, useMemo } from 'react';
import { Users, Building2, ListMusic, TrendingUp, Activity, BarChart3, FileSpreadsheet, FileText } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import { MetricCard } from '@/components/molecules/MetricCard';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Select } from '@/components/atoms/Select';
import { Button } from '@/components/atoms/Button';
import { useBackofficeMetrics, useBackofficeRecentActivity, useChartData } from '@/hooks/useBackofficeData';
import styles from './DashboardPage.module.scss';

const METRIC_OPTIONS = [
  { label: 'Nuevos usuarios', value: 'users' },
  { label: 'Historias completadas', value: 'completions' },
  { label: 'Ingresos', value: 'revenue' },
];

const PERIOD_OPTIONS = [
  { label: 'Últimos 7 días', value: '7d' },
  { label: 'Últimos 30 días', value: '30d' },
  { label: 'Últimos 90 días', value: '90d' },
  { label: 'Último año', value: '1y' },
];

const METRIC_LABELS: Record<string, string> = {
  users: 'Nuevos usuarios',
  completions: 'Historias completadas',
  revenue: 'Ingresos ($)',
};

export function DashboardPage() {
  const { metrics, isLoading } = useBackofficeMetrics();
  const { activities } = useBackofficeRecentActivity();

  const chartRef = useRef<HTMLDivElement>(null);

  const [chartMetric, setChartMetric] = useState<'users' | 'completions' | 'revenue'>('users');
  const [chartPeriod, setChartPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  const { data: chartData, isLoading: isLoadingChart } = useChartData(chartMetric, chartPeriod);

  const formattedChartData = useMemo(
    () =>
      chartData.map((item) => ({
        date: new Date(item.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
        value: item.value,
      })),
    [chartData]
  );

  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Download Excel function with chart image
  const handleDownloadExcel = async () => {
    if (!chartRef.current) return;

    try {
      setIsDownloadingExcel(true);

      // Capture the chart as an image
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
      });

      const chartImageUrl = canvas.toDataURL('image/png');

      // Create a new workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Knooowy Stories Backoffice';
      workbook.created = new Date();

      // Add worksheet
      const worksheet = workbook.addWorksheet('Informe analíticas');

      // Add title
      worksheet.mergeCells('A1:B1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = '📊 Informe de analíticas de plataforma';
      titleCell.font = { size: 16, bold: true };
      titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

      // Add subtitle
      worksheet.mergeCells('A2:B2');
      const subtitleCell = worksheet.getCell('A2');
      subtitleCell.value = `Métrica: ${METRIC_LABELS[chartMetric]} | Período: ${PERIOD_OPTIONS.find((p) => p.value === chartPeriod)?.label} | Generado: ${new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`;
      subtitleCell.font = { size: 10, color: { argb: 'FF6B7280' } };
      subtitleCell.alignment = { vertical: 'middle', horizontal: 'left' };

      // Add empty row
      worksheet.addRow([]);

      // Calculate statistics
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const average = (total / chartData.length).toFixed(1);
      const peak = Math.max(...chartData.map(d => d.value));

      // Add statistics row
      const statsRow = worksheet.addRow(['TOTAL', 'MEDIA POR DÍA', 'VALOR MÁXIMO']);
      statsRow.font = { bold: true, size: 10, color: { argb: 'FF6B7280' } };
      statsRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF9FAFB' },
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const valuesRow = worksheet.addRow([total.toLocaleString(), average, peak.toLocaleString()]);
      valuesRow.font = { bold: true, size: 14 };
      valuesRow.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      // Add empty row
      worksheet.addRow([]);

      // Add data table headers
      const headerRow = worksheet.addRow(['Fecha', METRIC_LABELS[chartMetric]]);
      headerRow.font = { bold: true, size: 11 };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF3B82F6' },
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF3B82F6' } },
          bottom: { style: 'thin', color: { argb: 'FF3B82F6' } },
          left: { style: 'thin', color: { argb: 'FF3B82F6' } },
          right: { style: 'thin', color: { argb: 'FF3B82F6' } },
        };
      });

      // Add data rows
      chartData.forEach((item) => {
        const row = worksheet.addRow([item.date, item.value]);
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
            right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          };
          if (colNumber === 2) {
            cell.alignment = { horizontal: 'center' };
          }
        });
      });

      // Set column widths
      worksheet.getColumn(1).width = 25;
      worksheet.getColumn(2).width = 20;

      // Create a second worksheet for the chart
      const chartWorksheet = workbook.addWorksheet('Chart');

      // Add title to chart sheet
      chartWorksheet.mergeCells('A1:F1');
      const chartTitleCell = chartWorksheet.getCell('A1');
      chartTitleCell.value = '📊 Gráfico visual';
      chartTitleCell.font = { size: 16, bold: true };
      chartTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };

      // Add chart image
      const imageId = workbook.addImage({
        base64: chartImageUrl,
        extension: 'png',
      });

      // Insert image in the chart worksheet (starting at row 3, column A)
      chartWorksheet.addImage(imageId, {
        tl: { col: 0, row: 2 }, // top-left: column A, row 3
        ext: { width: 800, height: 450 }, // larger dimensions for better visibility
      });

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Download file
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `analytics-${chartMetric}-${chartPeriod}-${new Date().toISOString().split('T')[0]}.xlsx`
      );
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download Excel:', error);
      alert('Error al generar el archivo Excel. Inténtalo de nuevo.');
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  // Download PDF function with chart image (using jsPDF)
  const handleDownloadPDF = async () => {
    if (!chartRef.current) return;

    try {
      setIsDownloadingPDF(true);

      // Capture the chart as an image
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
      });

      const chartImageUrl = canvas.toDataURL('image/png');

      // Calculate statistics
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const average = (total / chartData.length).toFixed(1);
      const peak = Math.max(...chartData.map(d => d.value));

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Add title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Informe de analíticas de plataforma', 20, 20);

      // Add subtitle
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128); // gray
      const subtitle = `Métrica: ${METRIC_LABELS[chartMetric]} | Período: ${PERIOD_OPTIONS.find((p) => p.value === chartPeriod)?.label} | Generado: ${new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`;
      pdf.text(subtitle, 20, 28);

      // Add horizontal line
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.5);
      pdf.line(20, 32, 190, 32);

      // Add summary cards
      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      const yStart = 42;

      // Card 1: Total
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(20, yStart, 50, 20, 2, 2, 'F');
      pdf.text('TOTAL ' + METRIC_LABELS[chartMetric], 22, yStart + 5);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(17, 24, 39);
      pdf.text(total.toLocaleString(), 22, yStart + 13);

      // Card 2: Average
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.roundedRect(75, yStart, 50, 20, 2, 2, 'F');
      pdf.text('MEDIA POR DÍA', 77, yStart + 5);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(17, 24, 39);
      pdf.text(average, 77, yStart + 13);

      // Card 3: Peak
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.roundedRect(130, yStart, 50, 20, 2, 2, 'F');
      pdf.text('VALOR MÁXIMO', 132, yStart + 5);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(17, 24, 39);
      pdf.text(peak.toLocaleString(), 132, yStart + 13);

      // Add chart image
      const imgWidth = 170;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(chartImageUrl, 'PNG', 20, 72, imgWidth, imgHeight);

      // Add footer
      const footerY = 72 + imgHeight + 10;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.text('Generado por Knooowy Stories Backoffice Platform', 105, footerY, { align: 'center' });
      pdf.text(`© ${new Date().getFullYear()} Knooowy. Todos los derechos reservados.`, 105, footerY + 4, { align: 'center' });

      // Save PDF
      const filename = `analytics-${chartMetric}-${chartPeriod}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Error al generar el PDF. Inténtalo de nuevo.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  if (isLoading || !metrics) {
    return (
      <div className={styles.dashboard}>
        <h1>Dashboard</h1>
        <p>Cargando métricas...</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboard__header}>
        <h1>Dashboard</h1>
        <p className={styles.dashboard__subtitle}>Analíticas y gestión de la plataforma</p>
      </div>

      <div className={styles.dashboard__metrics}>
        <MetricCard
          icon={Users}
          label="Total usuarios"
          value={metrics.totalUsers.toString()}
          description={`${metrics.activeUsers} activos`}
          variant="primary"
        />
        <MetricCard
          icon={Building2}
          label="Total empresas"
          value={metrics.totalCompanies.toString()}
          description={`${metrics.activeCompanies} activas`}
          variant="success"
        />
        <MetricCard
          icon={ListMusic}
          label="Total historias"
          value={metrics.totalStories.toString()}
          description={`${metrics.publishedStories} publicados`}
        />
        <MetricCard
          icon={TrendingUp}
          label="Crecimiento mensual"
          value={`+${metrics.monthlyGrowth}%`}
          description={`$${metrics.totalRevenue.toLocaleString()} ingresos`}
          variant="success"
        />
      </div>

      <div className={styles.dashboard__stats}>
        <Card>
          <CardHeader>
            <CardTitle>Estadísticas de historias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={styles.dashboard__statGrid}>
              <div className={styles.dashboard__statItem}>
                <span className={styles.dashboard__statLabel}>Completados totales</span>
                <span className={styles.dashboard__statValue}>
                  {metrics.totalStoriesCompleted.toLocaleString()}
                </span>
              </div>
              <div className={styles.dashboard__statItem}>
                <span className={styles.dashboard__statLabel}>Tasa media de finalización</span>
                <span className={styles.dashboard__statValue}>{metrics.averageCompletionRate}%</span>
              </div>
              <div className={styles.dashboard__statItem}>
                <span className={styles.dashboard__statLabel}>Historias en borrador</span>
                <span className={styles.dashboard__statValue}>{metrics.draftStories}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className={styles.dashboard__charts}>
        <Card>
          <CardHeader>
            <CardTitle>
              <BarChart3 className={styles.dashboard__activityIcon} />
              Analíticas de plataforma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={styles.dashboard__chartControls}>
              <Select
                label="Métrica"
                options={METRIC_OPTIONS}
                value={chartMetric}
                onValueChange={(value) => setChartMetric(value as 'users' | 'completions' | 'revenue')}
              />
              <Select
                label="Período"
                options={PERIOD_OPTIONS}
                value={chartPeriod}
                onValueChange={(value) => setChartPeriod(value as '7d' | '30d' | '90d' | '1y')}
              />
              <Button
                variant="outline"
                size="md"
                leftIcon={<FileSpreadsheet size={16} />}
                onClick={handleDownloadExcel}
                disabled={isDownloadingExcel || isLoadingChart}
              >
                {isDownloadingExcel ? 'Descargando...' : 'Excel'}
              </Button>
              <Button
                variant="outline"
                size="md"
                leftIcon={<FileText size={16} />}
                onClick={handleDownloadPDF}
                disabled={isDownloadingPDF || isLoadingChart}
              >
                {isDownloadingPDF ? 'Descargando...' : 'PDF'}
              </Button>
            </div>

            {isLoadingChart ? (
              <div className={styles.dashboard__chartLoading}>Cargando datos del gráfico...</div>
            ) : (
              <div ref={chartRef} className={styles.dashboard__chartWrapper}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formattedChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickLine={{ stroke: '#d1d5db' }}
                    />
                    <YAxis
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickLine={{ stroke: '#d1d5db' }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px 12px',
                      }}
                      labelStyle={{ color: '#111827', fontWeight: 600, marginBottom: '4px' }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '16px' }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name={METRIC_LABELS[chartMetric]}
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className={styles.dashboard__activity}>
        <CardHeader>
          <CardTitle>
            <Activity className={styles.dashboard__activityIcon} />
            Actividad reciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <p className={styles.dashboard__emptyState}>Sin actividad reciente</p>
          ) : (
            <div className={styles.dashboard__activityList}>
              {activities.map((activity) => (
                <div key={activity.id} className={styles.dashboard__activityItem}>
                  <div className={styles.dashboard__activityInfo}>
                    <span className={styles.dashboard__activityDescription}>
                      {activity.description}
                    </span>
                    <span className={styles.dashboard__activityTime}>
                      {new Date(activity.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DashboardPage;
